import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api, isManualPlatform, type RedeemApp } from '@/api/client'
import { useRedeemApps, useRedeemPosts } from '@/lib/queries'
import { PLATFORM_LABEL, sameProduct, useInvalidateRedeem, where } from '@/lib/redeem'
import { Button, ConfirmDialog, CopyButton, TonePill, Textarea, When, errorText, useToast } from '@/ui'
import { Section, unslottedNote } from './shared'

/** Mirrors the server's DefaultEndNote. */
const DEFAULT_NOTE = 'This giveaway has ended. Thanks to everyone who grabbed a code! Feedback and questions are still welcome.'

/** What to put up by hand on the channels the desk does not post to. */
const MANUAL_HINT: Record<string, string> = {
  ppomppu: '제목 앞에 [종료]를 붙이고, 본문 첫 줄에 아래 문구',
  okky: '제목 앞에 [종료]를 붙이고, 본문 첫 줄에 아래 문구',
  x: '원글에 아래 문구로 답글',
  threads: '원글에 아래 문구로 답글',
  instagram: '캡션 첫 줄이나 댓글로 아래 문구',
  tiktok: '고정 댓글로 아래 문구',
}
const KOREAN_NOTE = '나눔은 종료되었습니다. 참여해 주셔서 감사합니다!'
const isKorean = (p: string) => p === 'ppomppu' || p === 'okky'
const NOTE_MAX = 300

/** Ending a giveaway: the desk stops handing out, tells late askers it is
 *  over, and leaves the closing note under each of its posts over the next
 *  few ticks. Manual channels get the text to paste. */
export default function EndGiveaway({ app }: { app: RedeemApp }) {
  const [note, setNote] = useState(app.end_note || DEFAULT_NOTE)
  const [asking, setAsking] = useState<'end' | 'reopen' | null>(null)
  const ended = !!app.ended_at
  const [unschedule, setUnschedule] = useState(true)
  const published = useRedeemPosts(app.id, 'published')
  const scheduledQ = useRedeemPosts(app.id, 'scheduled')
  const scheduled = scheduledQ.data?.items ?? []
  // Other rows of the same product that keep running after this one ends.
  const keepRunning = (useRedeemApps().data?.items ?? [])
    .map((i) => i.app)
    .filter((a) => sameProduct(a, app) && a.status === 'active' && !a.ended_at)
  const invalidate = useInvalidateRedeem()
  const toast = useToast()
  const posts = published.data?.items ?? []
  const desk = posts.filter((p) => !isManualPlatform(p.platform))
  const manual = posts.filter((p) => isManualPlatform(p.platform))

  const end = useMutation({
    mutationFn: () => api.endRedeemGiveaway(app.id, note, unschedule && scheduled.length > 0),
    onSuccess: async (res) => {
      toast.success(res.unscheduled ? `프로모션을 종료했다. 예약된 글 ${res.unscheduled}개를 내렸다` : '프로모션을 종료했다')
      setAsking(null)
      await invalidate()
    },
    onError: (e) => {
      toast.error(errorText(e))
      setAsking(null)
    },
  })
  const reopen = useMutation({
    mutationFn: () => api.reopenRedeemGiveaway(app.id),
    onSuccess: async (r) => {
      toast.success(`다시 열었다. 코드 발급이 재개된다.${unslottedNote(r.unslotted)}`)
      setAsking(null)
      await invalidate()
    },
    onError: (e) => {
      toast.error(errorText(e))
      setAsking(null)
    },
  })
  const retry = useMutation({
    mutationFn: (id: number) => api.retryRedeemPostClose(id),
    onSuccess: async () => {
      toast.success('종료 댓글을 다시 시도했다')
      await invalidate()
    },
    onError: (e) => toast.error(errorText(e)),
  })

  return (
    <Section
      title={
        <span className="flex flex-wrap items-center gap-2">
          프로모션 종료
          {ended && <TonePill tone="publishing" label={`종료됨`} />}
          {ended && <When iso={app.ended_at} className="text-meta font-normal text-muted-foreground" />}
        </span>
      }
      hint="종료하면 코드 발급이 바로 멈추고, 이후 요청에는 종료됐다고 한 번 답한다(Reddit 답글, Bluesky DM). Reddit·Bluesky 글에는 아래 문구를 종료 댓글로 3분마다 두 개씩 단다. 글은 지우지 않는다."
    >
      {!ended && (
        <>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} aria-label="종료 댓글 문구" />
          <div className={note.length > NOTE_MAX ? 'text-meta text-status-failed' : 'text-meta text-muted-foreground'}>
            {note.length} / {NOTE_MAX} (Bluesky 제한)
          </div>
          <Button variant="danger" disabled={!note.trim() || note.length > NOTE_MAX} onClick={() => setAsking('end')}>
            프로모션 종료…
          </Button>
        </>
      )}

      {ended && (
        <>
          <div className="whitespace-pre-wrap rounded-md bg-muted px-3 py-2 text-body">{app.end_note}</div>
          {desk.length > 0 && (
            <ul className="space-y-1.5 text-table">
              {desk.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-muted-foreground">
                    {p.platform === 'reddit' ? `r/${p.channel}` : 'Bluesky'} · {p.title}
                  </span>
                  {p.closed_at ? (
                    <a href={p.close_url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                      종료 댓글 확인
                    </a>
                  ) : p.close_error ? (
                    <>
                      <span className="break-all text-status-failed" title={p.close_error}>
                        실패: {p.close_error.slice(0, 80)}
                      </span>
                      <Button size="sm" loading={retry.isPending && retry.variables === p.id} onClick={() => retry.mutate(p.id)}>
                        다시 시도
                      </Button>
                    </>
                  ) : (
                    <span className="text-muted-foreground">대기 중</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {manual.length > 0 && (
            <div className="space-y-3">
              <div className="text-meta font-medium">직접 처리할 채널</div>
              {manual.map((p) => {
                const text = isKorean(p.platform) ? KOREAN_NOTE : app.end_note
                return (
                  <div key={p.id} className="space-y-1.5 text-table">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-medium">{PLATFORM_LABEL[p.platform]}</span>
                      {p.external_url && (
                        <a href={p.external_url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                          글 열기
                        </a>
                      )}
                      <span className="text-meta text-muted-foreground">{MANUAL_HINT[p.platform]}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1 whitespace-pre-wrap rounded-md bg-muted px-3 py-2">{text}</div>
                      <CopyButton text={text} what="종료 문구" label="복사" />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          <Button onClick={() => setAsking('reopen')} title="다시 코드를 나눠 준다. 이미 단 종료 댓글은 그대로 남는다.">
            다시 열기
          </Button>
        </>
      )}

      <ConfirmDialog
        open={asking === 'end'}
        onOpenChange={(o) => !o && !end.isPending && setAsking(null)}
        title={`${app.name} (${app.slug}) 나눔을 종료할까?`}
        description={`이 줄만 끝난다: ${app.name} (${app.slug}). 코드 발급이 바로 멈추고, 올라간 글 ${desk.length}개에 종료 댓글을 단다.`}
        body={
          <div className="space-y-3 text-table">
            {keepRunning.length > 0 && (
              <div>
                <div className="font-medium">같은 상품의 다른 나눔은 계속 돈다</div>
                <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                  {keepRunning.map((a) => (
                    <li key={a.id}>
                      {a.name} ({a.slug})
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {scheduled.length > 0 && (
              <div>
                <label className="flex items-center gap-2 font-medium">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[rgb(var(--accent))]"
                    checked={unschedule}
                    onChange={(e) => setUnschedule(e.target.checked)}
                  />
                  예약된 글 {scheduled.length}개도 내린다
                </label>
                <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                  {scheduled.map((p) => (
                    <li key={p.id}>
                      {where(p)} · {p.title || '(제목 없음)'}
                      {p.scheduled_at && <> · <When iso={p.scheduled_at} /></>}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-meta text-muted-foreground">
                  {unschedule
                    ? '글은 지우지 않고 초안으로 돌린다.'
                    : '내리지 않아도 이 줄이 끝나면 예약 글은 나가지 않고 그대로 남는다.'}
                </p>
              </div>
            )}
          </div>
        }
        confirmLabel="종료"
        confirmVariant="destructive"
        loading={end.isPending}
        onConfirm={async () => {
          await end.mutateAsync()
        }}
      />
      <ConfirmDialog
        open={asking === 'reopen'}
        onOpenChange={(o) => !o && !reopen.isPending && setAsking(null)}
        title={`${app.name} 나눔을 다시 열까?`}
        description="코드 발급이 재개된다. 이미 단 종료 댓글은 그대로 남는다."
        confirmLabel="다시 열기"
        loading={reopen.isPending}
        onConfirm={async () => {
          await reopen.mutateAsync()
        }}
      />
    </Section>
  )
}
